// All user-facing Russian copy in one place — see docs/SPEC.md §4.
// Never hardcode strings in JSX; add them here first.

export const copy = {
  common: {
    continue: "Продолжить",
    back: "Назад",
    somethingWrong: "Что-то пошло не так",
    loading: "Загружаем…",
    retry: "Попробовать ещё раз",
  },

  s1: {
    title: "Паспорт искателя ждёт владельца",
    subtitle: "Опишите искателя — и паспорт станет именным",
    cta: "Ввести код квеста",
    stamp: "НЕ АКТИВИРОВАН",
  },

  // D-011: parent enters the 4-char activation code the admin printed
  // for the quest — no more date/time picker.
  s2: {
    title: "Введите код квеста",
    subtitle: "Четыре символа с бейджа ведущего или на монете",
    codePlaceholder: "AB12",
    cta: "Подтвердить",
    confirmed: (venue: string) => `Квест: ${venue}`,
  },

  s3: {
    title: "Опишите искателя",
    childName: "Имя ребёнка",
    childBirthdate: "Дата рождения",
    childBirthdateHint: "Пришлём подарок ко дню рождения",
    phone: "Ваш телефон",
    phoneFromProfile: "Взять из профиля",
    email: "Email",
    emailHint: "Пришлём ссылку, если сменишь телефон или устройство",
    consentPd: "Согласен на обработку персональных данных",
    consentMedia: "Разрешаю публикацию видео с игры",
    addSecondChild: "Добавить второго ребёнка",
    cta: "Выдать паспорт",

    // D-011: shown after we recognise the parent by phone.
    knownParent: {
      hint: "Мы вас узнали. У вас уже есть паспорт на этот телефон:",
      sameChild: (childName: string) => `Отметить посещение — ${childName}`,
      otherChild: "Другой ребёнок",
    },

    // D-004 layer 3: cross-device recovery via email magic-link.
    restore: {
      link: "У меня уже был паспорт",
      title: "Восстановление паспорта",
      hint: "Введите email, который вы указывали при первой активации — пришлём ссылку для входа.",
      emailLabel: "Email",
      submit: "Отправить ссылку",
      cancel: "Назад",
      sent: (email: string) =>
        `Готово! Ссылка отправлена на ${email}. Проверьте почту (и папку «Спам»).`,
    },

    // Banners for /?magic=<reason> — the /consume endpoint bounces here
    // on failure so the SPA can show a friendly message.
    magicBanner: {
      invalid: "Ссылка недействительна. Попробуйте запросить новую.",
      not_found: "Ссылка не найдена. Возможно, её уже использовали.",
      expired: "Срок действия ссылки истёк. Запросите новую.",
      used: "Ссылка уже использована.",
    },
  },

  s4: {
    titleWithName: (name: string) => `${name} теперь официальный искатель`,
    watchReel: "Смотреть видео с квеста",
    reelPending: "Рилс ещё не загрузился — появится в течение часа",
    share: "Рассказать о квесте",
    // CTA that opens the standalone bonuses page. Kept short so it
    // reads as a next step, not as a settings entry.
    openBonuses: "Забрать подарки",
    backToPassport: "Назад к паспорту",
  },

  // S4b — bonuses + partner promos, shown just below the passport book.
  // Bonuses come with a redemption code the parent shows the admin at the
  // venue. Self-brand promos are grouped with bonuses; partner promos live
  // below with a "cкидки от друзей" heading.
  // Small "delete my data" affordance at the very bottom of S4 —
  // 152-ФЗ mandates it be accessible, but we don't want it competing
  // for attention with the CTAs above.
  gdpr: {
    trigger: "Удалить паспорт и мои данные",
    dialogTitle: "Удалить все данные?",
    dialogHint:
      "Мы вышлем ссылку на почту, привязанную к паспорту. После подтверждения удалим паспорт, фото ребёнка и вашу учётку. Отменить будет нельзя.",
    dialogConfirm: "Прислать ссылку на удаление",
    dialogCancel: "Отмена",
    sent:
      "Проверьте почту — там ссылка для подтверждения удаления. " +
      "Действует 30 минут.",
    noEmail:
      "На паспорте нет email — напишите администратору, он удалит данные вручную.",
    genericError:
      "Не смогли отправить ссылку. Попробуйте позже или напишите администратору.",
  },

  bonuses: {
    heading: "Ваши бонусы",
    empty: "Пока нет бонусов — они появятся после первой активации",
    forChild: (name: string) => `для ${name}`,
    codeLabel: "Код для администратора",
    codePending: "Код уточните у администратора при бронировании",
    expiresLabel: (until: string) => `Действует до ${until}`,
    usedLabel: (when: string) => `Использован ${when}`,
    partnerHeading: "Скидки от друзей",
    partnerEmpty: "Партнёрские предложения появятся здесь",
    validUntil: (until: string) => `до ${until}`,
    open: "Открыть",
    copyCode: "Копировать код",
    codeCopied: "Скопировано",
  },

  // S7 — sharing prompt with 3 editable templates. §11.2 requires them
  // to be tweakable so parents can pick the vibe (mom-chat / class-chat
  // / neutral). Link is refCode-based → /r/:code lands invitees on the
  // inviter's city; the resulting Referral row is what booking-side
  // attribution reads later.
  s7: {
    title: "Расскажите в чате",
    hint: "Выберите текст — или отредактируйте по-своему.",
    templateA: (name: string) =>
      `Мы были на квесте «Легенда об искателях» — ${name} теперь официальный искатель с паспортом. По ссылке вашему ребёнку тоже выдадут паспорт и апгрейд к первому празднику.`,
    templateB: (name: string) =>
      `Ходили с ${name} на квест — детям очень зашло. Кто ещё не был — по ссылке дают паспорт искателя и подарок на первый праздник.`,
    templateC: () =>
      `Классный квест для детей — прошли всей семьёй, будем повторять. По ссылке дадут паспорт и приятный бонус к первому празднику.`,
    picker: {
      a: "Мама-чат",
      b: "Класс-чат",
      c: "Нейтральный",
    },
    copyLink: "Скопировать ссылку",
    share: "Отправить",
    copied: "Скопировано",
    stats: (shared: number, booked: number) =>
      `Переслали: ${shared} · Забронировали: ${booked}`,
  },

  // S6 — quick feedback after the parent has seen the passport / reel.
  // Positive path bounces them to 2GIS or Yandex Maps (per §11 отзыв
  // без вознаграждения — площадки за это банят). Negative path stays
  // internal — text goes to the admin dashboard.
  s6: {
    prompt: "Как вам приключение?",
    positive: "Понравилось",
    negative: "Что-то пошло не так",

    positiveTitle: "Расскажете другим родителям?",
    positiveHint: "Отзыв на карте помогает другим мамам найти нас.",
    gis: "Оставить в 2ГИС",
    yandex: "Оставить в Яндекс.Картах",
    positiveThanks: "Спасибо! Ждём вас снова.",

    negativeTitle: "Расскажите, что было не так",
    negativeHint: "Прочитает лично управляющий, не публикуем.",
    negativePlaceholder: "Опишите ситуацию…",
    negativeSubmit: "Отправить",
    negativeThanks: "Спасибо, разберёмся.",

    skip: "Позже",
  },

  passport: {
    seriesNumber: "СЕРИЯ И НОМЕР",
    issueDate: "ДАТА ВЫДАЧИ",
    issuePlace: "МЕСТО ВЫДАЧИ",
    rank: "РАНГ",
    name: "ИМЯ",
    birthdate: "ДАТА РОЖДЕНИЯ",
    header1: "ЛЕГЕНДА ОБ ИСКАТЕЛЯХ",
    header2: "ПАСПОРТ ИСКАТЕЛЯ",
    rankNovice: "Новичок",
    rankExplorer: "Искатель",
    rankMaster: "Мастер",
    rankGuardian: "Хранитель",
  },

  errors: {
    CITY_NOT_FOUND: "Город не найден. Проверьте ссылку на монете.",
    GAME_NOT_FOUND: "Квест не найден.",
    CODE_NOT_FOUND: "Такого кода нет. Проверьте у ведущего.",
    CODE_EXPIRED: "Срок действия монеты истёк. Обратитесь к администратору квеста.",
    LOCKED_OUT: "Слишком много попыток. Попробуйте через 15 минут.",
    QUOTA_EXCEEDED: "Активации закрыты для этого квеста.",
    ALREADY_ACTIVATED: "Этот телефон уже связан с паспортом для этого квеста.",
    WINDOW_EXPIRED: "Квест был больше 30 дней назад — активация невозможна.",
    CONSENT_REQUIRED: "Отметьте согласие на обработку данных.",
    VALIDATION_ERROR: "Проверьте заполненные поля.",
    SESSION_NOT_FOUND: "Сессия истекла. Отсканируйте монету заново.",
    SESSION_EXPIRED: "Сессия истекла. Отсканируйте монету заново.",
    INTERNAL_ERROR: "Что-то пошло не так. Попробуйте ещё раз.",
    NETWORK: "Нет связи. Проверьте интернет и попробуйте ещё раз.",
  } satisfies Record<string, string>,
} as const;

export type ErrorCode = keyof typeof copy.errors;

export function errorText(code: string): string {
  const known = copy.errors[code as ErrorCode];
  return known ?? copy.errors.INTERNAL_ERROR;
}
